import { Link } from "@tanstack/react-router";
import { ideas, ideaBySlug } from "@/content/en/ideas";
import { conceptBySlug } from "@/content/en/concepts";
import { frameworks } from "@/content/en/frameworks";
import portrait from "@/assets/portrait.jpg";
import cop31Logo from "@/assets/cop31-logo.png.asset.json";
import { ExploreMap } from "@/components/en/ExploreMap";
import { cop31NotesSorted, cop31NotePath } from "@/lib/cop31-notes";

// Signature concepts to surface on the homepage, in author-chosen order.
const SIGNATURE_SLUGS = [
  "epistemic-weaponization",
  "algorithmic-surrender",
  "efficiency-debt",
  "commitment-debt",
  "ai-sustainability-paradox",
] as const;

// COP31 Antalya opening day. Until the summit closes, COP31 leads the homepage.
const COP31_START = new Date("2026-11-09T00:00:00Z");
const COP31_END = new Date("2026-11-20T23:59:59Z");

function cop31Countdown(now: Date) {
  if (now > COP31_END) return null;
  const days = Math.ceil((COP31_START.getTime() - now.getTime()) / 86_400_000);
  if (days > 0) return `${days} days to Antalya`;
  return "Live in Antalya";
}

export function EnHome() {
  const featured = ideaBySlug("can-ai-solve-the-climate-crisis") ?? ideas[0];
  const recent = ideas.slice(0, 3);
  const signatureConcepts = SIGNATURE_SLUGS.map((s) => conceptBySlug(s)).filter(
    (c): c is NonNullable<typeof c> => Boolean(c),
  );
  const cop31Label = cop31Countdown(new Date());
  const cop31Notes = cop31NotesSorted.slice(0, 3);

  return (
    <div className="max-w-6xl mx-auto px-6">
      {/* 0 — COP31: dominant opening block until the summit closes */}
      {cop31Label && (
        <section className="pt-10 md:pt-14 pb-14 md:pb-20">
          <div className="bg-ink text-paper p-7 md:p-12">
            <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start">
              <div className="md:col-span-7">
                <div className="flex flex-wrap items-center gap-4 text-[10px] uppercase tracking-[0.3em] font-semibold">
                  <span className="text-clay">COP31 · Antalya</span>
                  <span className="text-paper/60">9–20 November 2026</span>
                  <span className="border border-paper/30 px-3 py-1 text-paper/85">
                    {cop31Label}
                  </span>
                </div>
                <h1 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.03] tracking-tight text-balance">
                  COP31 Antalya: reading between the lines of the negotiation.
                </h1>
                <p className="mt-6 max-w-2xl text-base md:text-lg text-paper/75 leading-relaxed text-pretty">
                  A continuously updated guide to the summit: verified
                  essentials with their official sources, Türkiye's commitment
                  architecture, and an analysis layer on climate finance, the
                  water agenda, and the gap between pledge and implementation.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-6 text-[11px] uppercase tracking-[0.22em] font-bold">
                  <Link
                    to="/cop31-guide"
                    className="border-b-2 border-clay pb-1 hover:text-clay transition-colors"
                  >
                    All COP31 notes →
                  </Link>
                </div>
              </div>
              <div className="md:col-span-5">
                <img
                  src={cop31Logo.url}
                  alt="COP31 Türkiye Antalya logo"
                  decoding="async"
                  width={480}
                  height={270}
                  className="w-full max-w-[260px] h-auto bg-paper p-3"
                />
                <p className="mt-6 text-[10px] uppercase tracking-[0.28em] text-clay font-semibold">
                  Latest notes
                </p>
                <ul className="mt-4 space-y-4">
                  {cop31Notes.map((n) => (
                    <li key={n.slug}>
                      <Link
                        to={cop31NotePath(n.slug, "en")}
                        className="group block border-t border-paper/20 pt-3"
                      >
                        <span className="text-[10px] uppercase tracking-[0.22em] text-paper/50">
                          {n.dateEn}
                        </span>
                        <span className="mt-1 block font-serif text-lg leading-snug text-paper/90 group-hover:text-clay transition-colors text-balance">
                          {n.titleEn}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 0b — COP31 writing first, then recent essays */}
      {cop31Label && (
        <section className="pb-14 md:pb-20 border-b border-ink/10">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-clay font-semibold">
                COP31
              </p>
              <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight italic">
                The latest COP31 writing.
              </h2>
            </div>
            <Link
              to="/cop31-guide"
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ink/25 text-[11px] uppercase tracking-[0.22em] hover:bg-ink hover:text-paper transition-colors"
            >
              All COP31 notes →
            </Link>
          </div>
          <div className="grid gap-y-12">
            {cop31NotesSorted.slice(0, 1).map((n) => (
              <Link key={n.slug} to={cop31NotePath(n.slug, "en")} className="group block max-w-3xl">
                <span className="text-[10px] uppercase tracking-[0.24em] text-clay font-semibold">
                  {n.dateEn}
                </span>
                <h3 className="mt-3 font-serif text-3xl md:text-4xl leading-snug text-ink group-hover:text-clay transition-colors text-balance">
                  {n.titleEn}
                </h3>
                <p className="mt-4 text-[15px] md:text-base text-ink/75 leading-relaxed">
                  {n.summaryEn}
                </p>
                <span className="mt-4 inline-block text-[10px] uppercase tracking-[0.22em] font-bold border-b border-ink/20 group-hover:border-clay pb-0.5">
                  COP31 note · Read →
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* 0c — Recent essays, straight from the homepage */}
      <section className="py-14 md:py-20 border-b border-ink/10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-clay font-semibold">
              Recent
            </p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight italic">
              The last three essays.
            </h2>
          </div>
          <Link
            to="/ideas"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ink/25 text-[11px] uppercase tracking-[0.22em] hover:bg-ink hover:text-paper transition-colors"
          >
            All essays →
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-x-8 gap-y-12">
          {recent.map((r) => (
            <Link
              key={r.slug}
              to="/ideas/$slug"
              params={{ slug: r.slug }}
              className="group block"
            >
              <span className="text-[10px] uppercase tracking-[0.24em] text-clay font-semibold">
                {r.topics[0]}
              </span>
              <h3 className="mt-3 font-serif text-2xl leading-snug text-ink group-hover:text-clay transition-colors text-balance">
                {r.title}
              </h3>
              <p className="mt-3 text-[14px] text-ink/75 leading-relaxed line-clamp-3">
                {r.subtitle}
              </p>
              <span className="mt-4 inline-block text-[10px] uppercase tracking-[0.22em] font-bold border-b border-ink/20 group-hover:border-clay pb-0.5">
                {r.readingMinutes} min · Read →
              </span>
            </Link>
          ))}
        </div>
      </section>


      {/* 1 — Author + thesis */}
      <section className="pt-14 md:pt-20 pb-14 md:pb-20">

        <p className="text-[10px] uppercase tracking-[0.32em] text-clay font-semibold">
          Arda Öztaşkın · Essays
        </p>
        {(() => {
          const Heading = cop31Label ? "h2" : "h1";
          return (
            <Heading className="mt-6 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-balance max-w-4xl">
              Smart Sustainability — essays on AI, judgment, and the climate era.
              <span className="block mt-4 text-clay text-3xl md:text-5xl">When intelligence becomes abundant, judgment becomes the scarce resource.</span>
            </Heading>
          );
        })()}

        <p className="mt-8 max-w-2xl text-lg md:text-xl text-ink/75 leading-relaxed text-pretty">
          The English writing of Arda Öztaşkın — independent essays, original
          concepts, and working frameworks on artificial intelligence,
          sustainability, institutional trust, and the future of human
          judgment.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-6 text-[11px] uppercase tracking-[0.22em] font-semibold">
          <Link
            to="/ideas"
            className="border-b-2 border-clay pb-1 hover:text-clay transition-colors"
          >
            Read the essays →
          </Link>
          <Link
            to="/about"
            className="border-b border-ink/25 pb-1 hover:border-clay hover:text-clay transition-colors"
          >
            About the author
          </Link>
        </div>
      </section>

      {/* 2 — Central question */}
      <section className="py-14 md:py-20 border-t border-ink/10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="text-[10px] uppercase tracking-[0.28em] text-clay font-semibold">
              The central question
            </p>
          </div>
          <div className="md:col-span-8">
            <p className="font-serif text-2xl md:text-[2rem] leading-snug text-ink text-balance">
              How do societies keep judgment intact when technology accelerates
              faster than the institutions built to govern it — and faster
              than the habits of mind built to understand it?
            </p>
            <p className="mt-6 text-base text-ink/70 leading-relaxed max-w-2xl">
              This site is an attempt to answer that question in public: one
              essay, one concept, one framework at a time. The tools of
              analysis are named, not borrowed. The arguments are meant to be
              disagreed with.
            </p>
          </div>
        </div>
      </section>





      {/* 3 — Explore the work (idea-first intellectual map) */}
      <ExploreMap />


      {/* 4 — Signature concepts */}
      <section className="py-14 md:py-20 border-t border-ink/10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-clay font-semibold">
              Signature concepts
            </p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight">
              A working vocabulary.
            </h2>
            <p className="mt-3 text-[15px] text-ink/70 leading-relaxed max-w-2xl">
              Original terms developed across the essays. Each links to the
              full entry and to the essays and frameworks that use it.
            </p>
          </div>
          <Link
            to="/concepts"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ink/25 text-[11px] uppercase tracking-[0.22em] hover:bg-ink hover:text-paper transition-colors"
          >
            All concepts →
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
          {signatureConcepts.map((c) => (
            <Link
              key={c.slug}
              to="/concepts/$slug"
              params={{ slug: c.slug }}
              className="group block border-t border-ink/70 pt-5 hover:text-clay transition-colors"
            >
              <span className="text-[10px] uppercase tracking-[0.24em] text-clay font-semibold">
                Concept
              </span>
              <h3 className="mt-2 font-serif text-[22px] md:text-2xl italic leading-snug text-ink group-hover:text-clay text-balance">
                {c.name}
              </h3>
              <p className="mt-3 text-[14px] text-ink/75 leading-relaxed line-clamp-4">
                {c.oneLine}
              </p>
              <span className="mt-4 inline-block text-[10px] uppercase tracking-[0.22em] font-bold border-b border-ink/20 group-hover:border-clay pb-0.5">
                Read the entry →
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-10 md:hidden">
          <Link
            to="/concepts"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ink/25 text-[11px] uppercase tracking-[0.22em]"
          >
            All concepts →
          </Link>
        </div>
      </section>

      {/* 5 — Featured essay */}
      <section className="py-14 md:py-20 border-t border-ink/10">
        <p className="text-[10px] uppercase tracking-[0.28em] text-clay font-semibold">
          Featured essay
        </p>
        <div className="mt-6 grid md:grid-cols-12 gap-10 md:gap-12 items-start">
          <div className="md:col-span-7">
            <Link
              to="/ideas/$slug"
              params={{ slug: featured.slug }}
              className="group block"
            >
              <div className="aspect-[3/2] w-full bg-ink text-paper p-10 flex flex-col justify-between overflow-hidden">
                <span className="text-[10px] uppercase tracking-[0.28em] text-paper/60 font-semibold">
                  {featured.topics.join(" · ")}
                </span>
                <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight text-balance text-paper/95 group-hover:text-paper transition-colors">
                  “{featured.title}”
                </h2>
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-paper/60">
                  <span className="h-px w-10 bg-paper/40" />
                  {featured.readingMinutes} min essay
                </div>
              </div>
            </Link>
          </div>
          <div className="md:col-span-5 flex flex-col gap-6 md:pt-6">
            <p className="text-base md:text-lg text-ink/80 leading-relaxed text-pretty">
              {featured.abstract.split("\n\n")[0]}
            </p>
            <Link
              to="/ideas/$slug"
              params={{ slug: featured.slug }}
              className="self-start text-xs uppercase tracking-[0.2em] font-bold border-b-2 border-clay pb-1 hover:text-clay transition-colors"
            >
              Read the essay →
            </Link>
          </div>
        </div>
      </section>

      {/* 6 — Frameworks */}
      <section className="py-14 md:py-20 border-t border-ink/10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-clay font-semibold">
              Frameworks
            </p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight">
              Tools for reading a claim.
            </h2>
            <p className="mt-3 text-[15px] text-ink/70 leading-relaxed max-w-2xl">
              Original models used across the essays — audits, tests, and
              accounting rules for arguments that usually escape scrutiny.
            </p>
          </div>
          <Link
            to="/frameworks"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ink/25 text-[11px] uppercase tracking-[0.22em] hover:bg-ink hover:text-paper transition-colors"
          >
            All frameworks →
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {frameworks.slice(0, 3).map((f) => (
            <Link
              key={f.slug}
              to="/frameworks/$slug"
              params={{ slug: f.slug }}
              className="group flex flex-col border border-ink/15 hover:border-ink p-7 transition-colors"
            >
              <span className="text-[10px] uppercase tracking-[0.24em] text-clay font-semibold">
                Framework
              </span>
              <h3 className="mt-3 font-serif text-xl md:text-2xl leading-snug text-balance group-hover:text-clay transition-colors">
                {f.name}
              </h3>
              <p className="mt-3 font-serif italic text-[15px] text-clay/90 leading-snug">
                {f.coreQuestion}
              </p>
              <p className="mt-3 text-[14px] text-ink/70 leading-relaxed line-clamp-3">
                {f.shortDescription}
              </p>
              <span className="mt-6 inline-block text-[10px] uppercase tracking-[0.22em] font-bold border-b border-ink/20 group-hover:border-clay pb-0.5 self-start">
                Read framework →
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* 7 — moved: recent essays now appear near the top, under COP31 */}


      {/* 8 — About Arda */}
      <section className="py-14 md:py-20 border-t border-ink/10 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <img
            src={portrait}
            alt="Arda Öztaşkın — portrait"
            loading="lazy"
            decoding="async"
            width={512}
            height={512}
            className="w-40 h-40 md:w-full md:h-auto md:max-w-[280px] object-cover grayscale"
          />
        </div>
        <div className="md:col-span-8">
          <p className="text-[10px] uppercase tracking-[0.28em] text-clay font-semibold">
            About the author
          </p>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight">
            Arda Öztaşkın
          </h2>
          <p className="mt-5 text-base md:text-lg text-ink/80 leading-relaxed max-w-2xl">
            Writer on artificial intelligence, sustainability, and the future
            of human judgment. Author of three books, including
            <em> The Loneliness Economy</em>. This site is the English edition
            of an independent, single-author publication — no institution, no
            editorial board, no house style but the argument.
          </p>
          <p className="mt-4 text-base text-ink/70 leading-relaxed max-w-2xl">
            The work sits between essay and philosophy of technology: closer in
            temperament to <em>Aeon</em>, <em>Noema</em>, and the long form of
            Paul Graham than to a research center or a product blog.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-6">
            <Link
              to="/about"
              className="text-xs uppercase tracking-[0.22em] font-bold border-b-2 border-clay pb-1 hover:text-clay transition-colors"
            >
              Read the full bio →
            </Link>
            <a
              href="https://www.linkedin.com/in/ardaoztaskin/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold border-b border-ink/25 hover:border-clay pb-1"
            >
              LinkedIn <span aria-hidden>↗</span>
            </a>
            <a
              href="https://www.amazon.com/stores/Arda-%C3%96zta%C5%9Fk%C4%B1n/author/B0H596GS9Q"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold border-b border-ink/25 hover:border-clay pb-1"
            >
              Books <span aria-hidden>↗</span>
            </a>
          </div>
        </div>
      </section>

      {/* 9 — Monthly dispatch */}
      <section className="pb-24 pt-6">
        <div className="bg-ink text-paper p-8 md:p-12 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <div className="max-w-xl">
            <p className="text-[10px] uppercase tracking-[0.28em] text-paper/60 font-semibold">
              Monthly dispatch
            </p>
            <h3 className="mt-4 font-serif text-3xl md:text-4xl italic leading-tight">
              One letter a month. Nothing else.
            </h3>
            <p className="text-[15px] text-paper/80 mt-4 leading-relaxed">
              A new essay, a concept worth naming, and a short reading of the
              reports that matter — sent once a month. No tracking beacons, no
              cross-posts, no growth loops. You can leave at any time.
            </p>
          </div>
          <Link
            to="/newsletter"
            className="w-full md:w-auto bg-paper text-ink font-bold text-[11px] uppercase tracking-[0.24em] py-3 px-6 hover:opacity-90 transition-opacity text-center"
          >
            Subscribe
          </Link>
        </div>
      </section>
    </div>
  );
}
